import React, { useState, useEffect, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const StatusBadge = ({ status, expiry }) => {
  const isExpired = new Date(expiry) < new Date();
  const effectiveStatus = isExpired ? 'expired' : status;
  const config = {
    active: { bg: 'bg-green-100', text: 'text-green-700', label: 'Active' },
    inactive: { bg: 'bg-gray-100', text: 'text-gray-600', label: 'Inactive' },
    expired: { bg: 'bg-red-100', text: 'text-red-600', label: 'Expired' },
  };
  const c = config?.[effectiveStatus] || config?.inactive;
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${c?.bg} ${c?.text}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${effectiveStatus === 'active' ? 'bg-green-500' : effectiveStatus === 'expired' ? 'bg-red-500' : 'bg-gray-400'}`} />
      {c?.label}
    </span>
  );
};

const UsageBar = ({ count, limit }) => {
  const pct = limit > 0 ? Math.min((count / limit) * 100, 100) : 0;
  const color = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-amber-500' : 'bg-green-500';
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs text-gray-500">
        <span>{count?.toLocaleString()}</span>
        <span>{limit?.toLocaleString()}</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-500 ${color}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const PromoCodesTable = ({ refreshTrigger }) => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortField, setSortField] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [toast, setToast] = useState('');

  const fetchCodes = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      let query = supabase?.from('promo_codes')?.select('*')?.order(sortField, { ascending: sortDir === 'asc' });
      if (filterStatus !== 'all') query = query?.eq('promo_status', filterStatus);
      const { data, error: err } = await query;
      if (err) throw err;
      setCodes(data || []);
    } catch (err) {
      setError(err?.message || 'Failed to load promo codes');
    } finally {
      setLoading(false);
    }
  }, [filterStatus, sortField, sortDir]);

  useEffect(() => { fetchCodes(); }, [fetchCodes, refreshTrigger]);

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3000); };

  const handleToggleStatus = async (code) => {
    const newStatus = code?.promo_status === 'active' ? 'inactive' : 'active';
    setActionLoading(code?.id);
    try {
      const { error: err } = await supabase?.from('promo_codes')?.update({ promo_status: newStatus })?.eq('id', code?.id);
      if (err) throw err;
      showToast(`Code "${code?.code_name}" ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      fetchCodes();
    } catch (err) {
      showToast(`Error: ${err?.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Delete promo code "${code?.code_name}"? This cannot be undone.`)) return;
    setActionLoading(code?.id);
    try {
      const { error: err } = await supabase?.from('promo_codes')?.delete()?.eq('id', code?.id);
      if (err) throw err;
      showToast(`Code "${code?.code_name}" deleted`);
      fetchCodes();
    } catch (err) {
      showToast(`Error: ${err?.message}`);
    } finally {
      setActionLoading(null);
    }
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  };

  const SortIcon = ({ field }) => (
    <Icon name={sortField === field ? (sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'} size={14} className="text-gray-400" />
  );

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {toast && (
        <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-sm px-4 py-2.5 rounded-xl shadow-lg animate-fade-in">
          {toast}
        </div>
      )}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(15,32,68,0.08)' }}>
            <Icon name="List" size={18} style={{ color: '#0f2044' }} />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">Active Promo Codes</h2>
          <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">{codes?.length}</span>
        </div>
        <div className="flex items-center gap-2">
          {['all', 'active', 'inactive', 'expired']?.map(s => (
            <button
              key={s}
              onClick={() => setFilterStatus(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${filterStatus === s ? 'text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              style={filterStatus === s ? { background: '#0f2044' } : {}}
            >
              {s?.charAt(0)?.toUpperCase() + s?.slice(1)}
            </button>
          ))}
          <button onClick={fetchCodes} className="p-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors">
            <Icon name="RefreshCw" size={14} className="text-gray-600" />
          </button>
        </div>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Icon name="Loader2" size={24} className="animate-spin text-gray-400" />
        </div>
      ) : error ? (
        <div className="flex flex-col items-center justify-center py-16 gap-2">
          <Icon name="AlertCircle" size={32} className="text-red-400" />
          <p className="text-sm text-red-500">{error}</p>
          <button onClick={fetchCodes} className="text-xs text-blue-600 hover:underline">Retry</button>
        </div>
      ) : codes?.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 gap-2">
          <Icon name="Tag" size={32} className="text-gray-300" />
          <p className="text-sm text-gray-500">No promo codes found</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b border-gray-100">
                {[
                  { label: 'Code', field: 'code_name' },
                  { label: 'Discount', field: 'discount_value' },
                  { label: 'Service', field: 'service_type' },
                  { label: 'Usage', field: 'usage_count' },
                  { label: 'Expires', field: 'expiry_date' },
                  { label: 'Status', field: 'promo_status' },
                  { label: 'Actions', field: null },
                ]?.map(col => (
                  <th
                    key={col?.label}
                    onClick={() => col?.field && handleSort(col?.field)}
                    className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide ${col?.field ? 'cursor-pointer hover:text-gray-700 select-none' : ''}`}
                  >
                    <div className="flex items-center gap-1">
                      {col?.label}
                      {col?.field && <SortIcon field={col?.field} />}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {codes?.map(code => (
                <tr key={code?.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3">
                    <span className="font-mono font-semibold text-gray-900 tracking-wider">{code?.code_name}</span>
                    {code?.min_order_value > 0 && (
                      <p className="text-xs text-gray-400 mt-0.5">Min KES {code?.min_order_value?.toLocaleString()}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-semibold" style={{ color: '#D4AF37' }}>
                      {code?.discount_type === 'percentage' ? `${code?.discount_value}%` : `KES ${code?.discount_value}`}
                    </span>
                    <p className="text-xs text-gray-400 capitalize">{code?.discount_type}</p>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                      code?.service_type === 'ride' ? 'bg-blue-100 text-blue-700' :
                      code?.service_type === 'delivery'? 'bg-purple-100 text-purple-700' : 'bg-indigo-100 text-indigo-700'
                    }`}>
                      <Icon name={code?.service_type === 'ride' ? 'Car' : code?.service_type === 'delivery' ? 'Package' : 'Layers'} size={10} />
                      {code?.service_type === 'both' ? 'All' : code?.service_type?.charAt(0)?.toUpperCase() + code?.service_type?.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-3 min-w-[120px]">
                    <UsageBar count={code?.usage_count} limit={code?.usage_limit} />
                  </td>
                  <td className="px-4 py-3 text-gray-600 text-xs">
                    {new Date(code?.expiry_date)?.toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {new Date(code?.expiry_date) < new Date() && (
                      <p className="text-red-500 font-medium">Expired</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={code?.promo_status} expiry={code?.expiry_date} />
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleToggleStatus(code)}
                        disabled={actionLoading === code?.id || new Date(code?.expiry_date) < new Date()}
                        title={code?.promo_status === 'active' ? 'Deactivate' : 'Activate'}
                        className="p-1.5 rounded-lg transition-colors disabled:opacity-40 hover:bg-gray-100"
                      >
                        {actionLoading === code?.id ? (
                          <Icon name="Loader2" size={14} className="animate-spin text-gray-400" />
                        ) : (
                          <Icon name={code?.promo_status === 'active' ? 'PauseCircle' : 'PlayCircle'} size={14} className={code?.promo_status === 'active' ? 'text-amber-500' : 'text-green-500'} />
                        )}
                      </button>
                      <button
                        onClick={() => handleDelete(code)}
                        disabled={actionLoading === code?.id}
                        title="Delete"
                        className="p-1.5 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-40"
                      >
                        <Icon name="Trash2" size={14} className="text-red-400" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default PromoCodesTable;
